import classnames from 'classnames'
import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'

import styles from './tooltip.module.scss'

export enum TOP_TIP_POSITION {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

export interface TooltipProps {
  children: ReactNode
  className?: string
  isVisible: boolean
  topTipPosition?: TOP_TIP_POSITION
}

const Tooltip = ({ children, className, isVisible, topTipPosition = TOP_TIP_POSITION.TOP }: TooltipProps) => {
  const wrapperClassName = classnames(styles.tooltip, styles[`tooltip__${topTipPosition}`], className)

  if (!isVisible) return null
  return <span className={wrapperClassName}>{children}</span>
}

export default Tooltip

export interface TooltipWrapperProps {
  children: ReactElement
  className?: string
  toolTipContentClassName?: string
  topTipPosition?: TOP_TIP_POSITION
  toolTipContentRenderer: () => ReactNode
  dataTestId?: string
}

export const ToolTipWrapper = ({
  children,
  className,
  toolTipContentClassName,
  topTipPosition,
  toolTipContentRenderer,
  dataTestId,
}: TooltipWrapperProps) => {
  const [visible, setVisible] = useState(false)

  const toolTipWrapper = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleHover = () => {
      setVisible(true)
    }
    const handleHoverEnd = () => {
      setVisible(false)
    }
    const toolTipWrapperEl = toolTipWrapper.current
    toolTipWrapperEl?.addEventListener('mouseenter', handleHover)
    toolTipWrapperEl?.addEventListener('mouseleave', handleHoverEnd)

    return () => {
      toolTipWrapperEl?.removeEventListener('mouseenter', handleHover)
      toolTipWrapperEl?.removeEventListener('mouseleave', handleHoverEnd)
    }
  }, [])

  return (
    <div
      ref={toolTipWrapper}
      onClick={() => {
        setVisible(true)
      }}
      data-testid={dataTestId}
      className={classnames(styles.tooltipWrapper, className)}
    >
      {children}
      <Tooltip isVisible={visible} topTipPosition={topTipPosition} className={toolTipContentClassName}>
        {toolTipContentRenderer()}
      </Tooltip>
    </div>
  )
}
